import { useEffect } from 'react'

// Watch a menu / popover anchor element and invoke `close` when the anchor
// leaves the layout (e.g. a `display: none` ancestor swaps in on tab switch).
// Without this, MUI's Menu / Popper logs "The anchorEl prop provided to the
// component is invalid" because the DOM node is still there but its
// `offsetParent` is null.
//
// Uses `IntersectionObserver` for the change signal — cheap, no polling,
// no re-render churn. The `offsetParent === null` guard inside the
// callback keeps normal scroll-off-screen behaviour untouched (MUI's
// Popover handles that on its own), so we only auto-close when the
// anchor has actually been removed from the layout tree.
export function useCloseOnAnchorHidden(anchor: HTMLElement | null, close: () => void): void {
  useEffect(() => {
    if (!anchor) return
    const io = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const el = entry.target as HTMLElement
        if (!entry.isIntersecting && el.offsetParent === null) {
          close()
          break
        }
      }
    })
    io.observe(anchor)
    return () => io.disconnect()
  }, [anchor, close])
}
